import React, { useState, useEffect, useRef } from 'react';
import { Error as ErrorComponent, Success } from '../Form';
import { format } from 'date-fns';
import API from '../../api/api';
import { FaEllipsisH } from 'react-icons/fa';
import male from "../FamilyNode/male-avatar.png";
import female from "../FamilyNode/female-avatar.png";
import { auth } from '../../firebase.config';
import { useNavigate } from 'react-router-dom';

interface User {
  id: string;
  gender: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  dateOfBirth: string;
  city: string;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  isPaid: boolean;
  isPremium: boolean;
  email_verified: boolean;
  role?: string;
}

class AuthError extends globalThis.Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

const UsersList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isUpdatingRole, setIsUpdatingRole] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const [selectedRole, setSelectedRole] = useState<string>('client');
  const [gettingAllUsers, setGettingAllUsers] = useState(false);
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  useEffect(() => {
    fetchUsers();

    // Add click event listener to close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      // If dropdown is open and click is outside the dropdown menu, close it
      if (
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(event.target as Node)
      ) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // Mount-only: fetch users once and attach outside-click listener
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    if (!activeDropdown) setIsDeleting(null);
  }, [activeDropdown]);

  const getAuthToken = () => {
    // First try to get token from auth object
    if (auth.currentUser) {
      return auth.currentUser.getIdToken(true)
        .then(token => {
          // Save the token to localStorage for future use
          localStorage.setItem("token", JSON.stringify(token));
          return token;
        });
    } 
    
    // If not available, try localStorage
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      try {
        return Promise.resolve(JSON.parse(storedToken));
      } catch (e) {
        // If token is not valid JSON, return the raw string
        return Promise.resolve(storedToken.replace(/^"|"$/g, ''));
      }
    }
    
    // Return a rejected promise if no token is available
    return Promise.reject(new AuthError('No authentication token available'));
  };

  const fetchUsers = async () => {
    setGettingAllUsers(true);
    setError(null);
    
    try {
      const token = await getAuthToken();
      
      const response = await API.get('auth/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      setGettingAllUsers(false);
      setUsers(response.data.users);
      setLoading(false);
    } catch (err: any) {
      console.error("Error fetching users:", err);
      setError(err.response?.data?.message || 'Failed to fetch users. Please try logging out and back in.');
      setLoading(false);
      setGettingAllUsers(false);
    }
  };

  const handleRevokeAccess = async (userId: string) => {
    if (!window.confirm('Are you sure you want to revoke this user\'s premium access?')) return;
    
    try {
      setError(null);
      const token = await getAuthToken();
      
      // Call the revoke endpoint
      const response = await API.get(
        `/subscription/revoke/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
  
      if (response.status === 200) {
        // Update the user list to reflect the change
        setUsers(users.map(user => 
          user.id === userId ? { ...user, isPremium: false } : user
        ));
        
        setSuccess('User premium access revoked successfully');
        navigate("/dashboard", { replace: true });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to revoke user access');
    }
  };

  const handleRoleChange = async (newRole: string) => {
    if (!selectedUser) return;

    try {
      setError(null);
      setIsUpdatingRole(selectedUser.id);
      setSuccess(null);
      
      const token = await getAuthToken();
      
      await API.put(`auth/users/${selectedUser.id}/role`, 
        { newRole: newRole },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      setUsers(users.map(user => 
        user.id === selectedUser.id ? { ...user, role: newRole } : user
      ));
      setSuccess('User role updated successfully');
      setShowRoleModal(false);
      setSelectedUser(null);
    } catch (err: any) {
      setShowRoleModal(false);
      setSelectedUser(null);
      setError(err.response?.data?.message || 'Failed to update user role');
    } finally {
      setIsUpdatingRole(null);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    console.log("handleDeleteUser called", userId);
    // No confirm here, handled by modal
    // Store current state for potential rollback
    const previousUsers = users;
    try {
      setIsDeleting(userId);
      setError(null);
      // Optimistic update
      setUsers(prev => prev.filter(user => user.id !== userId));
      const token = await getAuthToken();
      console.log("About to delete user", userId);
      await API.delete(`auth/user/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setSuccess('User deleted successfully');
    } catch (err: any) {
      // Rollback on error
      setUsers(previousUsers);
      setError(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setIsDeleting(null);
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const formatDate = (timestamp: { _seconds: number; _nanoseconds: number }) => {
    return format(new Date(timestamp._seconds * 1000), 'MMM dd, yyyy');
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {error && <ErrorComponent>{error}</ErrorComponent>}
      {success && <Success>{success}</Success>}

      {/* Stats Cards */}
      <div className="row mb-4 g-3">
        {/* Total Users Card */}
        <div className="col-md-4">
          <div className="card h-100 border">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="card-title text-muted mb-0 text-uppercase small">Total Users</h6>
                <span className="badge bg-opacity-10 text-primary p-2">
                  <i className="fas fa-users"></i>
                </span>
              </div>
              <h2 className="fw-bold mb-1">{users.length}</h2>
            </div>
          </div>
        </div>

        {/* Premium Users Card */}
        <div className="col-md-4">
          <div className="card h-100 border">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="card-title text-muted mb-0 text-uppercase small">Premium Users</h6>
                <span className="badge bg-success bg-opacity-10 text-success p-2">
                  <i className="fas fa-crown"></i>
                </span>
              </div>
              <h2 className="fw-bold mb-1">{users.filter(user => user.isPremium).length}</h2>
            </div>
          </div>
        </div>

        {/* Admin Users Card */}
        <div className="col-md-4">
          <div className="card h-100 border">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="card-title text-muted mb-0 text-uppercase small">Administrators</h6>
                <span className="badge bg-info bg-opacity-10 text-info p-2">
                  <i className="fas fa-user-shield"></i>
                </span>
              </div>
              <h2 className="fw-bold mb-1">{users.filter(user => user.role === 'admin').length}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* Retry button for failed fetch */}
      {error && (
        <div className="mb-3">
          <button 
            className="btn btn-primary" 
            onClick={() => fetchUsers()}
            disabled={gettingAllUsers}
          >
            {gettingAllUsers ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Retrying...
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt me-2"></i>
                Retry Fetching Users
              </>
            )}
          </button>
        </div>
      )}

      {/* Users Table */}
      <div className="card">
        <div className="card-header bg-white">
          <h5 className="mb-0">Users List</h5>
        </div>
        <div className="card-body p-0">
          <div 
            className="table-responsive" 
            style={{ 
              height: '60vh',
              overflowY: 'auto',
              overflowX: 'auto'
            }}
          >
            <table className="table table-hover mb-0" style={{ width: 'auto', minWidth: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                <tr>
                  <th style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>Name</th>
                  <th style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>Email</th>
                  <th style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>Phone</th>
                  <th style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>City</th>
                  <th style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>Joined Date</th>
                  <th style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>Status</th>
                  <th style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>Activated</th>
                  <th style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>Role</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  console.log( "user", user?.email_verified);
                  return (
                  <tr key={user.id}>
                    <td style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>
                      <div className="d-flex align-items-center">
                        <img
                          src={user.gender === 'male' ? male : female}
                          alt="avatar"
                          className="rounded-circle me-2"
                          style={{ width: '32px', height: '32px', flexShrink: 0 }}
                        />
                        {user.firstName} {user.lastName}
                      </div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>{user.email}</td>
                    <td style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>{user.phoneNumber}</td>
                    <td style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>{user.city}</td>
                    <td style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>{formatDate(user.createdAt)}</td>
                    <td style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>
                      <span className={`badge ${user.isPremium ? 'bg-success' : 'bg-warning'}`}>
                        {user.isPremium ? 'Premium' : 'Free'}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>
                      <span className={`badge ${user.role === 'admin' ? 'bg-danger' : 'bg-secondary'}`}>
                        {user.role || 'user'}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', paddingRight: '20px' }}>
                      <span className={`badge ${user.role === 'admin' ? 'bg-danger' : 'bg-secondary'}`}>
                        {user.role || 'user'}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div className="position-relative" ref={dropdownRef}>
                        <button
                          className="btn btn-sm btn-link text-dark p-0"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setActiveDropdown(activeDropdown === user.id ? null : user.id);
                          }}
                          disabled={isDeleting === user.id || isUpdatingRole === user.id}
                        >
                          <FaEllipsisH />
                        </button>
                        {activeDropdown === user.id && (
                          <div 
                            ref={dropdownMenuRef}
                            className="dropdown-menu show position-absolute end-0 mt-1"
                            style={{
                              minWidth: '160px',
                              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                              border: '1px solid rgba(0,0,0,0.1)',
                              borderRadius: '4px',
                              zIndex: 1000
                            }}
                          >
                            <button
                              className="dropdown-item d-flex align-items-center py-2"
                              onClick={(e) => {
                                e.preventDefault();
                                // e.stopPropagation();
                                setSelectedUser(user);
                                setActiveDropdown(null);
                                setShowRoleModal(true);
                              }}
                              disabled={isUpdatingRole === user.id}
                            >
                              {isUpdatingRole === user.id ? (
                                <>
                                  <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                  Updating...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-user-cog me-2 text-primary"></i>
                                  Change Role
                                </>
                              )}
                            </button>
                            
                            {user.isPremium && (
                              <button
                                className="dropdown-item d-flex align-items-center py-2"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRevokeAccess(user.id);
                                  setActiveDropdown(null);
                                }}
                              >
                                <i className="fas fa-user-lock me-2"></i>
                                Revoke Premium
                              </button>
                            )}
                            
                            <button
                              className="dropdown-item d-flex align-items-center py-2 text-danger"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setUserToDelete(user);
                                setShowDeleteModal(true);
                              }}
                              disabled={isDeleting === user.id && activeDropdown === user.id}
                            >
                              {isDeleting === user.id && activeDropdown === user.id ? (
                                <>
                                  <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-trash-alt me-2"></i>
                                  Delete User
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Role Change Modal */}
      {showRoleModal && selectedUser && (
        <div 
          className="modal fade show" 
          style={{ 
            display: 'block',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10500
          }} 
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            style={{ zIndex: 10501 }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Change User Role</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowRoleModal(false);
                    setSelectedUser(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">User Information</label>
                  <div className="d-flex align-items-center mb-3">
                    <img
                      src={selectedUser.gender === 'male' ? male : female}
                      alt="avatar"
                      className="rounded-circle me-3"
                      style={{ width: '48px', height: '48px' }}
                    />
                    <div>
                      <h6 className="mb-0">{selectedUser.firstName} {selectedUser.lastName}</h6>
                      <small className="text-muted">{selectedUser.email}</small>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label htmlFor="roleSelect" className="form-label">Select New Role</label>
                  <select 
                    className="form-select" 
                    id="roleSelect"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    disabled={isUpdatingRole === selectedUser.id}
                  >
                    <option value="client">Client</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="alert alert-info">
                  <small>
                    <i className="fas fa-info-circle me-2"></i>
                    Admin: Full system access<br />
                    Manager: Limited administrative access<br />
                    Client: Standard user access
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowRoleModal(false);
                    setSelectedUser(null);
                  }}
                  disabled={isUpdatingRole === selectedUser.id}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    if (selectedUser) {
                      handleRoleChange(selectedRole);
                    }
                  }}
                  disabled={isUpdatingRole === selectedUser?.id}
                >
                  {isUpdatingRole === selectedUser?.id ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && userToDelete && (
        <div 
          className="modal fade show" 
          style={{ 
            display: 'block',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10500
          }} 
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            style={{ zIndex: 10501 }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                  }}
                  disabled={isDeleting === userToDelete.id}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete <strong>{userToDelete.firstName} {userToDelete.lastName}</strong>?</p>
                <p className="text-danger mb-0"><small>This action cannot be undone.</small></p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                  }}
                  disabled={isDeleting === userToDelete.id}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={async () => {
                    await handleDeleteUser(userToDelete.id);
                    setActiveDropdown(null);
                  }}
                  disabled={isDeleting === userToDelete.id}
                >
                  {isDeleting === userToDelete.id ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;